import React, { useEffect, useState } from 'react'
import { Box, Button, Card, CardContent, CircularProgress, IconButton, InputAdornment, Paper, Stack, TextField, Typography, useTheme } from '@mui/material'
import AddWebhookDialog from './addWebhookDialog'
import WebhookLogsDialog from './webhookLogsDialog'
import { SBSearch } from 'src/assets/sidebar/SBSearch'
import { useSelector } from 'react-redux'
import { useGetAllIntegrationLogsMutation, useGetAllWebhooksMutation } from 'src/services/integration-service'
import PremiumFeatureDialog from 'src/utils/premiumFeatureDialog'
import DeleteWebhookDialog from './deleteWebhookDialog'
import { toast } from 'react-hot-toast'

const WebhookPage = ({ canEdit, isFreeUser }) => {
  const theme = useTheme()
  const workspace = useSelector((state) => state.workspace)
  const [getAllWebhooks, { isLoading: isLoadingIntegrationWebhook }] = useGetAllWebhooksMutation()
  const [getAllIntegrationLogs, { isLoading: isLoadingIntegrationLogs }] = useGetAllIntegrationLogsMutation()
  const [integration, setIntegration] = useState(false)
  const [webhookIntegration, setWebhookIntegration] = useState([])
  const [openAddWebhookDialog, setOpenAddWebhookDialog] = useState(false)
  const [openWebhoooklogsDialog, setOpenWebhoooklogsDialog] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [workspaceId, setWorkspaceId] = useState()
  const [campiagnId, setCampaignId] = useState('')
  const [integrationLogs, setIntegrationLogs] = useState([])
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [totalLogs, setTotalLogs] = useState(0)
  const [webhookIdToDelete, setWebhookIdToDelete] = useState(null)
  const [webhookIdToDeleteName, setWebhookIdToDeleteName] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [openModal, setOpenModal] = useState(false)
  const [eventType, setEventType] = useState(null)

  const handleGetAllWebhook = async () => {
    try {
      const allWebhook = await getAllWebhooks().unwrap()
      setWebhookIntegration(allWebhook)
    } catch (error) {
      toast.error('Failed to fetch webhook. Please try again.', { duration: 2000 })
      throw error
    }
  }

  useEffect(() => {
    handleGetAllWebhook()
  }, [integration])

  useEffect(() => {
    setWorkspaceId(workspace?._id)
  }, [workspace])

  const handleOpenAddWebhookDialog = () => {
    if (isFreeUser) {
      setOpenModal(true)
      return
    }
    setOpenAddWebhookDialog(true)
  }

  const handleGetAllIntegrationLogs = async (id, event) => {
    try {
      const LogsLimit = 10
      const offset = integrationLogs.length
      const allIntegration = await useGetAllIntegrationLogsMutation({
        id: id ?? campiagnId,
        eventType: event ?? eventType,
        limit: LogsLimit,
        offset
      }).unwrap()
      setIntegrationLogs(allIntegration?.docs)
      setTotalLogs(allIntegration?.totalDocs)
    } catch (error) {
      toast.error('Failed to fetch logs. Please try again.', { duration: 2000 })
      throw error
    }
  }

  useEffect(() => {
    const object = {}
    object.id = campiagnId
    object.offset = integrationLogs.length * (page - 1)
    object.limit = limit
    getAllIntegrationLogs(object).then((res) => {
      setTotalLogs(res?.data?.totalDocs)
      setIntegrationLogs(res?.data?.docs)
    })
  }, [limit, page])

  const handleLimitChange = (event) => {
    setLimit(event.target.value)
    setPage(1)
  }

  const handleOpenWebhookLogsDialog = () => {
    setOpenWebhoooklogsDialog(true)
  }
  const handleCloseWebhookLogsDialog = () => {
    setOpenWebhoooklogsDialog(false)
    setIntegrationLogs([])
    setCampaignId('')
    setPage(1)
    setLimit(10)
  }

  const handleOpenDeleteModal = (webhook) => {
    const id = webhook?._id
    setOpenDeleteModal(true)
    setWebhookIdToDeleteName(webhook?.campaignName)
    setWebhookIdToDelete(id)
  }

  const handleWebhookSearch = (event) => {
    if (!event.target.value) {
      handleGetAllWebhook()
      return
    }
    setSearchTerm(event.target.value.toLowerCase())
    const filteredWebhook = webhookIntegration.filter(webhook =>
      webhook?.campaignName?.toLowerCase().includes(searchTerm) ||
      webhook?.eventType?.toLowerCase().includes(searchTerm) ||
      webhook?.webhookUrl?.toLowerCase().includes(searchTerm)
    )
    setWebhookIntegration(filteredWebhook)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  return (
    <Paper elevation={0} sx={{ mt: '24px', p: '24px', border: '1px solid #D7D9E6', borderRadius: '8px', minWidth: '100%' }}>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, md: 4 }, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: '16px', lg: '0px' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontSize: { xs: '14px', lg: '18px' }, fontWeight: 700 }}>Webhook</Typography>
          <Typography color='text.secondary' sx={{ fontSize: { xs: '12px', lg: '13px' }, fontWeight: 500 }}>
            Notify external services when an event occurs in your Successai workspace.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexDirection: { xs: 'column', md: 'row' }, width: { xs: '100%', md: 'auto' } }}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <IconButton sx={{ width: 32, height: 32 }}>
                    <SBSearch color='rgba(40, 40, 123, 0.5)' />
                  </IconButton>
                </InputAdornment>
              )
            }}
            variant='outlined'
            sx={{
              width: { xs: '100%', md: 'auto' },
              height: { xs: '38px', md: '42px' },
              border: 'none',
              backgroundColor: 'white',
              '& .MuiOutlinedInput-root': {
                height: '100%',
                '& > fieldset': { borderRadius: '8px', borderColor: theme.palette.grey[400] }
              },
              '& div': { pl: 0.3 },
              '& div input': {
                py: 1.8,
                fontSize: '13px',
                fontWeight: 400,
                lineHeight: '16px',
                letterSpacing: '0em',
                '&::placeholder': {
                  color: 'rgba(40, 40, 123, 0.5)'
                }
              }
            }}
            placeholder='Search webhook...'
            onChange={handleWebhookSearch}
          />
          <Button
            variant='contained'
            disableElevation
            sx={{
              height: { xs: '38px', md: '42px' },
              width: { xs: '100%', sm: '125px' },
              bgcolor: '#3366ff',
              fontSize: '14px',
              fontWeight: 600,
              borderRadius: '8px',
              padding: '10px 14px',
              '&:hover': { bgcolor: '#254EDB' }
            }}
            onClick={handleOpenAddWebhookDialog}
            disabled={!canEdit}
          >
            Add Webhook
          </Button>
        </Box>
      </Box>

      <Stack spacing={{ xs: '8px', md: '4px' }}>
        {isLoadingIntegrationWebhook
          ? (
            <Box display='flex' justifyContent='center' alignItems='center' style={{ height: '200px' }}>
              <CircularProgress />
            </Box>
            )
          : (
              webhookIntegration.map((webhook, index) => (
                <Card
                  key={index} variant='outlined'
                  sx={{
                    border: '1px solid #D7D9E6',
                    borderRadius: '8px',
                    height: { xs: 'auto', md: '72px' },
                    boxShadow: '0px 8px 34px 0px rgba(40, 40, 123, 0.1)'
                  }}
                >
                  <CardContent sx={{
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0px', width: { xs: '100%', md: 'auto' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography component='span' sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 600 }}>
                          {webhook?.campaignName}
                        </Typography>
                        <Typography component='span' color='text.secondary' sx={{ mx: 1 }}>
                          •
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '12px', md: '13px' }, fontWeight: 400 }}>
                          {webhook.eventType}
                        </Typography>
                      </Box>
                      <Box sx={{ maxWidth: { xs: '100%', md: 'none' }, overflow: 'hidden' }}>
                        <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: '12px', md: '13px' }, fontWeight: 400, mt: 0, overFlow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {webhook.webhookUrl}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: '4px', mt: { xs: '8px', md: 0 }, width: { xs: '100%', md: 'auto' }, justifyContent: 'space-between' }}>
                      <Button
                        variant='outlined'
                        onClick={() => {
                          setCampaignId(webhook?.campaignId)
                          handleOpenWebhookLogsDialog()
                          handleGetAllIntegrationLogs(webhook?.campaignId, webhook?.eventType)
                        }}
                        sx={{
                          border: '1px solid #E4E4E5',
                          borderRadius: '6px',
                          width: { xs: '137.5px', md: '98px' },
                          fontSize: { xs: '12px', md: '14px' },
                          fontWeight: 600,
                          color: 'black',
                          whiteSpace: 'nowrap' // prevent line break
                        }}
                      >
                        Show logs
                      </Button>
                      <Button
                        color='error'
                        sx={{
                          borderRadius: '6px',
                          fontSize: { xs: '12px', md: '14px' },
                          fontWeight: 600,
                          width: { xs: '137.5px', md: '74px' }
                        }}
                        onClick={() => { handleOpenDeleteModal(webhook) }}
                        disabled={!canEdit}
                      >
                        Delete
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
      </Stack>

      <AddWebhookDialog
        openAddWebhookDialog={openAddWebhookDialog}
        setOpenAddWebhookDialog={setOpenAddWebhookDialog}
        workspaceId={workspaceId}
        setIntegration={setIntegration}
        eventType={eventType}
        setEventType={setEventType}
      />
      <WebhookLogsDialog
        openWebhoooklogsDialog={openWebhoooklogsDialog}
        handleCloseWebhookLogsDialog={handleCloseWebhookLogsDialog}
        integrationLogs={integrationLogs}
        isLoadingIntegrationLogs={isLoadingIntegrationLogs}
        page={page}
        setPage={setPage}
        totalLogs={totalLogs}
        handleLimitChange={handleLimitChange}
        limit={limit}
      />
      <DeleteWebhookDialog
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        setIntegration={setIntegration}
        webhookIdToDelete={webhookIdToDelete}
        setWebhookIdToDelete={setWebhookIdToDelete}
        webhookIdToDeleteName={webhookIdToDeleteName}
      />
      <PremiumFeatureDialog
        open={openModal}
        onClose={handleCloseModal}
      />
    </Paper>
  )
}

export default WebhookPage
